import React, { useEffect, useState } from 'react';
import { fetchPublicLegal, type PublicLegalView } from './legalApi';

/**
 * Legal footer links, rendered from the workspace Legal config (by host).
 * Only configured links render — nothing is hardcoded and no other brand's
 * links ever appear. Internal pages are used when no external URL is set.
 */
export default function LegalFooter() {
  const [view, setView] = useState<PublicLegalView | null>(null);
  useEffect(() => {
    let alive = true;
    fetchPublicLegal().then(v => { if (alive) setView(v); });
    return () => { alive = false; };
  }, []);
  if (!view) return null;

  const links: { label: string; href: string }[] = [];
  const hasDoc = (doc: 'privacy' | 'terms' | 'refund' | 'cookie' | 'accessibility', slot: string) =>
    !!view.documents[doc]?.html || !!view.documentSlots[slot]?.active;
  links.push({ label: 'Privacy Policy', href: view.links.privacyUrl || (hasDoc('privacy', 'privacyPolicy') ? '/privacy' : '/privacy') });
  links.push({ label: 'Terms of Service', href: view.links.termsUrl || '/terms' });
  links.push({ label: 'Refund Policy', href: view.links.refundUrl || '/refund-policy' });
  links.push({ label: 'Cookie Policy', href: view.links.cookieUrl || '/cookies' });
  links.push({ label: 'Accessibility', href: view.links.accessibilityUrl || '/accessibility' });
  links.push({ label: 'Privacy Request', href: view.links.privacyRequestUrl || '/privacy-request' });
  if (view.links.doNotSellUrl) links.push({ label: 'Do Not Sell/Share My Info', href: view.links.doNotSellUrl });

  return (
    <footer className="text-xs text-slate-500" aria-label="Legal">
      <nav className="flex flex-wrap gap-x-4 gap-y-1.5">
        {links.map(l => (
          <a key={l.label} href={l.href} className="hover:underline hover:text-slate-700">
            {l.label}
          </a>
        ))}
      </nav>
      {view.postalAddress && <p className="mt-2 whitespace-pre-line">{view.postalAddress}</p>}
      {view.privacyContactEmail && (
        <p className="mt-1">
          Privacy contact:{' '}
          <a className="hover:underline" href={`mailto:${view.privacyContactEmail}`}>
            {view.privacyContactEmail}
          </a>
        </p>
      )}
    </footer>
  );
}
